/**
 * Condition evaluator for EntityForm conditional field logic.
 *
 * Evaluates field conditions against current form values to determine
 * field visibility, required state, disabled state, and other dynamic
 * properties.
 *
 * Operator evaluation is delegated to the shared conditionEvaluator
 * module, which is also used by DataList.
 *
 * The evaluator supports both simple conditions and nested
 * {@link ConditionGroup} trees with AND/OR semantics. A bare array shape
 * (the legacy form) is treated as implicit AND, so every existing form
 * config continues to behave identically.
 *
 * @see Spec sections 6.1-6.2 for condition definitions and evaluation rules.
 * @see `.claude-work/specs/component-rules.md` for the ConditionGroup
 *      wrapper that component rules reuse.
 */

import { evaluateOperator } from '../../shared/conditionEvaluator';
import type {
  ConditionEffect,
  ConditionGroup,
  FieldCondition,
  FieldConfig,
  FieldTypeOptions,
  RuleFieldCondition,
  SimpleCondition,
} from '../types';

// Re-export ConditionOperator so existing internal imports keep working
export type { ConditionOperator } from '../../shared/conditionEvaluator';

// ---------------------------------------------------------------------------
// Result type
// ---------------------------------------------------------------------------

/**
 * The merged result of evaluating all conditions for a field.
 */
export interface ConditionEvaluationResult {
  /** Whether the field should be visible */
  visible: boolean;
  /** Whether the field is required */
  required: boolean;
  /** Whether the field is disabled */
  disabled: boolean;
  /** Value to set when condition first becomes true */
  setValue?: unknown;
  /** Options override from the last matching condition */
  options?: FieldTypeOptions;
}

// ---------------------------------------------------------------------------
// Tree-shape discrimination
// ---------------------------------------------------------------------------

/**
 * Type guard that distinguishes a {@link ConditionGroup} from a
 * {@link SimpleCondition}. The discriminant is the `operator` property:
 * groups use `"AND" | "OR"`, leaves use one of the 11
 * {@link ConditionOperator} values.
 */
export function isConditionGroup(node: FieldCondition): node is ConditionGroup {
  return node.operator === 'AND' || node.operator === 'OR';
}

// ---------------------------------------------------------------------------
// Public API — simple condition evaluation
// ---------------------------------------------------------------------------

/**
 * Evaluates a single {@link FieldCondition} (simple or grouped) against the
 * current form values.
 *
 * This is the public entry point kept for backward compatibility with
 * callers that reason about conditions one-at-a-time. Groups are supported
 * transparently — an AND group returns true when every nested condition is
 * true, an OR group returns true when at least one is.
 *
 * @param condition - The condition (leaf or group) to evaluate.
 * @param values - Current form values keyed by field name.
 * @returns `true` if the condition is satisfied, `false` otherwise.
 */
export function evaluateCondition(
  condition: FieldCondition,
  values: Record<string, unknown>,
): boolean {
  if (isConditionGroup(condition)) {
    return evaluateGroup(condition, values);
  }
  return evaluateSimpleCondition(condition, values);
}

/**
 * Evaluates any supported condition-tree shape and returns a single boolean.
 *
 * Accepted inputs:
 * - `undefined`: vacuously `true` (no conditions → pass).
 * - {@link FieldCondition}`[]` (legacy): treated as an implicit AND group.
 *   An empty array is vacuously `true`.
 * - {@link ConditionGroup}: recurse through `conditions`, short-circuiting
 *   as soon as the result is determined. An AND group with zero conditions
 *   is vacuously `true`; an OR group with zero conditions is vacuously
 *   `false`.
 * - {@link SimpleCondition}: evaluated directly via the existing operator
 *   map. Behaviour is unchanged relative to the pre-backport evaluator.
 *
 * Short-circuit semantics:
 * - AND stops evaluating on the first `false` result.
 * - OR stops evaluating on the first `true` result.
 *
 * @param tree - The condition tree to evaluate.
 * @param values - Current form values keyed by field name.
 */
export function evaluateConditionTree(
  tree: FieldCondition | FieldCondition[] | undefined,
  values: Record<string, unknown>,
): boolean {
  if (tree === undefined) return true;
  if (Array.isArray(tree)) {
    // Empty array is vacuously true (matches the pre-backport behaviour
    // where "no conditions" meant the field used its base config).
    if (tree.length === 0) return true;
    return evaluateGroup({ operator: 'AND', conditions: tree }, values);
  }
  if (isConditionGroup(tree)) {
    return evaluateGroup(tree, values);
  }
  return evaluateSimpleCondition(tree, values);
}

// ---------------------------------------------------------------------------
// Internal helpers — tree walk
// ---------------------------------------------------------------------------

/**
 * Evaluates a {@link SimpleCondition} against the current form values.
 *
 * Kept as a private helper so the public `evaluateCondition` API can accept
 * the full {@link FieldCondition} union while this function receives a
 * narrowed simple condition.
 */
function evaluateSimpleCondition(
  condition: SimpleCondition,
  values: Record<string, unknown>,
): boolean {
  const fieldValue: unknown = values[condition.when];
  return evaluateOperator(fieldValue, condition.operator, condition.value);
}

/**
 * Evaluates a {@link ConditionGroup} against the current form values with
 * AND/OR short-circuit semantics.
 *
 * Short-circuit rules:
 * - AND: vacuously `true` when empty; on the first nested `false`, returns
 *   `false` without visiting the remaining conditions.
 * - OR: vacuously `false` when empty; on the first nested `true`, returns
 *   `true` without visiting the remaining conditions.
 */
function evaluateGroup(
  group: ConditionGroup,
  values: Record<string, unknown>,
): boolean {
  if (group.operator === 'AND') {
    for (const child of group.conditions) {
      if (!evaluateConditionTree(child, values)) {
        return false;
      }
    }
    return true;
  }

  // OR
  for (const child of group.conditions) {
    if (evaluateConditionTree(child, values)) {
      return true;
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// Effect collection — walks a tree and accumulates matched SimpleCondition
// effects while tracking whether the tree as a whole is satisfied.
// ---------------------------------------------------------------------------

/**
 * Recursively walks a tree and collects the {@link ConditionEffect} of
 * every {@link SimpleCondition} leaf whose predicate is `true`, in
 * depth-first order.
 *
 * This is used by the legacy `evaluateConditions` API path that merges
 * effects into a {@link ConditionEvaluationResult}. Collection is
 * intentionally separate from satisfaction — the caller decides whether the
 * tree as a whole is met (via {@link evaluateConditionTree}) and only then
 * applies the accumulated effects.
 *
 * For backward compatibility, the effect-collection walk always visits
 * every leaf (no short-circuit). Short-circuit semantics apply to
 * *evaluation* (boolean result) but not to *effect collection*. This
 * mirrors the pre-backport behaviour where each condition's effect was
 * considered independently.
 */
function collectLeafEffects(
  tree: FieldCondition | FieldCondition[] | undefined,
  values: Record<string, unknown>,
  out: ConditionEffect[],
): void {
  if (tree === undefined) return;

  if (Array.isArray(tree)) {
    for (const child of tree) {
      collectLeafEffects(child, values, out);
    }
    return;
  }

  if (isConditionGroup(tree)) {
    for (const child of tree.conditions) {
      collectLeafEffects(child, values, out);
    }
    return;
  }

  // SimpleCondition leaf
  if (evaluateSimpleCondition(tree, values)) {
    out.push(tree.effect);
  }
}

// ---------------------------------------------------------------------------
// Field-level evaluation
// ---------------------------------------------------------------------------

/**
 * Determines whether the base field config marks the field as required.
 * A field is required if `validation.required` is `true` or a non-empty
 * string (which serves as a custom i18n key).
 */
function isBaseRequired(baseConfig: FieldConfig): boolean {
  const req = baseConfig.validation?.required;
  return req === true || typeof req === 'string';
}

/**
 * Builds the default ConditionEvaluationResult from a field's static
 * configuration. These defaults are used when no conditions apply.
 */
function buildBaseResult(baseConfig: FieldConfig): ConditionEvaluationResult {
  return {
    visible: baseConfig.visible !== false,
    required: isBaseRequired(baseConfig),
    disabled: baseConfig.disabled === true,
  };
}

/**
 * Evaluates all conditions for a field and returns the merged effect.
 *
 * Evaluation rules (from spec section 6.2):
 * 1. If no conditions, field uses its base config.
 * 2. Conditions are evaluated in order; each is independent.
 * 3. Multiple conditions targeting the same property: the LAST matching wins.
 * 4. Base config values (visible, required, disabled) serve as defaults.
 * 5. When the overall condition tree is not satisfied (e.g. an AND group
 *    with one failing child, or an OR group with every child failing), the
 *    field reverts to its base config.
 *
 * The `conditions` parameter accepts either the legacy
 * {@link FieldCondition}`[]` form (implicit AND) or a single
 * {@link FieldCondition} (which may itself be a {@link ConditionGroup} for
 * AND/OR nesting).
 *
 * @param conditions - The condition tree attached to the field. May be an
 *   array (legacy implicit AND), a {@link ConditionGroup}, or a single
 *   {@link SimpleCondition}.
 * @param values - Current form values keyed by field name.
 * @param baseConfig - The field's static configuration, used for defaults.
 * @returns The merged condition evaluation result.
 */
export function evaluateConditions(
  conditions: FieldCondition | FieldCondition[] | undefined,
  values: Record<string, unknown>,
  baseConfig: FieldConfig,
): ConditionEvaluationResult {
  const result = buildBaseResult(baseConfig);

  // A vacuous / undefined / empty input means "no conditions" — return the
  // base config unchanged.
  if (conditions === undefined) return result;
  if (Array.isArray(conditions) && conditions.length === 0) return result;

  const allMet = evaluateConditionTree(conditions, values);

  // If the tree as a whole is not satisfied, revert to base config. This
  // preserves the pre-backport behaviour where a failing AND set reverted
  // to defaults.
  if (!allMet) return result;

  // Collect every leaf effect whose predicate is true (order preserved) and
  // apply them. "Last matching wins" semantics survive the tree walk since
  // traversal is depth-first in order.
  const matchedEffects: ConditionEffect[] = [];
  collectLeafEffects(conditions, values, matchedEffects);

  if (matchedEffects.length === 0) return result;

  applyEffects(result, matchedEffects);
  return result;
}

/**
 * Applies an array of condition effects to a result object.
 * Effects are applied in order so that the last matching effect wins
 * for each property.
 */
function applyEffects(
  result: ConditionEvaluationResult,
  effects: ConditionEffect[],
): void {
  for (const effect of effects) {
    if (effect.visible !== undefined) {
      result.visible = effect.visible;
    }
    if (effect.required !== undefined) {
      result.required = effect.required;
    }
    if (effect.disabled !== undefined) {
      result.disabled = effect.disabled;
    }
    if (effect.setValue !== undefined) {
      result.setValue = effect.setValue;
    }
    if (effect.options !== undefined) {
      result.options = effect.options;
    }
  }
}

// ---------------------------------------------------------------------------
// Rule-injected condition overlay
// ---------------------------------------------------------------------------

/**
 * Whether one overlay entry's predicate holds against the current form values.
 * Same leaf predicate `evaluateCondition` applies to a SimpleCondition — an
 * overlay entry is always a leaf, never a group.
 */
function matchesRuleCondition(
  condition: RuleFieldCondition,
  values: Record<string, unknown>,
): boolean {
  const fieldValue: unknown = values[condition.when];
  return evaluateOperator(fieldValue, condition.operator, condition.value);
}

/**
 * Applies the rule-injected condition **overlay**
 * ({@link FieldConfig.ruleConditions}) on top of an already-computed base
 * evaluation result.
 *
 * The overlay is a separate layer, NOT an extra branch of the field's own
 * condition tree — see {@link RuleFieldCondition} for why. Semantics:
 *
 * 1. The base result (from {@link evaluateConditions}, or the plain base
 *    config for a field with no conditions) is computed by the caller.
 * 2. Every entry whose predicate holds against the current form values applies
 *    its `visible` / `disabled` / `required` on top, in array order — so two
 *    entries touching the same property resolve "last matching wins", exactly
 *    like the base evaluator's leaf-effect merge.
 * 3. When no entry matches, the **same object** is returned (referential
 *    identity), so a field with no overlay — or an overlay that is currently
 *    inactive — is byte-identical to the pre-overlay behaviour.
 *
 * `effect.checked` is NOT a state and is ignored here: it is a one-shot value
 * write driven by {@link evaluateRuleCheckedStates} in `useFieldConditions`.
 * An entry carrying only `checked` therefore counts as "nothing applies".
 *
 * Pure: `result` is never mutated; a matching entry produces a shallow copy.
 *
 * @param result - The base evaluation result to overlay.
 * @param ruleConditions - The rule-injected overlay entries, if any.
 * @param values - Current form values keyed by field name.
 * @returns The overlaid result, or `result` itself when nothing applies.
 */
export function applyRuleConditions(
  result: ConditionEvaluationResult,
  ruleConditions: RuleFieldCondition[] | undefined,
  values: Record<string, unknown>,
): ConditionEvaluationResult {
  if (ruleConditions === undefined || ruleConditions.length === 0) {
    return result;
  }

  let overlaid: ConditionEvaluationResult | undefined;

  for (const condition of ruleConditions) {
    const { visible, disabled, required } = condition.effect;
    // A `checked`-only entry moves no state — skip it before copying so the
    // referential-identity guarantee holds for it too.
    if (visible === undefined && disabled === undefined && required === undefined) {
      continue;
    }
    if (!matchesRuleCondition(condition, values)) {
      continue;
    }

    overlaid ??= { ...result };
    if (visible !== undefined) {
      overlaid.visible = visible;
    }
    if (required !== undefined) {
      overlaid.required = required;
    }
    if (disabled !== undefined) {
      overlaid.disabled = disabled;
    }
  }

  return overlaid ?? result;
}

/**
 * The current state of one `checked`-carrying overlay entry, as reported by
 * {@link evaluateRuleCheckedStates}.
 */
export interface RuleCheckedState {
  /**
   * Identity of the entry within its field's overlay: its index plus its
   * serialised payload, so an entry that is replaced or edited in place (a
   * rule re-saved from the admin preview) reads as a NEW entry rather than a
   * transition of the old one.
   */
  key: string;
  /** The value to write to the target field on the predicate's rising edge. */
  checked: boolean;
  /** Whether the entry's predicate currently holds. */
  active: boolean;
}

/**
 * Reports, for every overlay entry that carries `effect.checked`, whether its
 * predicate currently holds — in array order, so a caller applying several
 * rising entries keeps "last matching wins". Entries without `checked` are
 * skipped (they are state-only and handled by {@link applyRuleConditions}).
 *
 * Pure and stateless: detecting the false → true **edge** (and deciding what
 * counts as a user edit rather than a load) is the caller's job — see the
 * `checked` side-effect in `useFieldConditions`.
 *
 * @param ruleConditions - The field's rule-injected overlay entries, if any.
 * @param values - Current form values keyed by field name.
 */
export function evaluateRuleCheckedStates(
  ruleConditions: RuleFieldCondition[] | undefined,
  values: Record<string, unknown>,
): RuleCheckedState[] {
  if (ruleConditions === undefined) return [];

  const states: RuleCheckedState[] = [];
  ruleConditions.forEach((condition, index) => {
    const { checked } = condition.effect;
    if (checked === undefined) return;
    states.push({
      key: `${String(index)}::${JSON.stringify(condition)}`,
      checked,
      active: matchesRuleCondition(condition, values),
    });
  });
  return states;
}
